import { useState, useRef } from 'react';
import {
  X,
  MapPin,
  ChevronDown,
  ChevronUp,
  Printer,
  Edit3,
  MessageSquare,
  MoreHorizontal,
  Camera,
  Upload,
  Package,
  Truck,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { useToast } from '../../components/boothly/toast-provider.jsx';
import { useConfirm } from '../../components/boothly/confirm-provider.jsx';
import { fmtCurrency } from '../../lib/format.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

const STATUS_ACTIONS = {
  Offen:     { label: 'Als bereit markieren',   color: 'var(--accent-troedel)', bg: 'rgba(127,119,221,1)'  },
  Bereit:    { label: 'Als versendet markieren', color: '#fff',    bg: 'rgba(48,209,88,1)'   },
  Versendet: { label: 'Retoure starten',          color: '#fff',    bg: 'rgba(255,69,58,1)'   },
  Retoure:   { label: 'Abgeschlossen',            color: '#fff',    bg: 'rgba(142,142,147,1)' },
};

const STATUS_NEXT = {
  Offen:     'Bereit',
  Bereit:    'Versendet',
  Versendet: 'Retoure',
};

const CAN_WRITE = (role) => role !== 'viewer' && role !== 'finance';

// ── Main component ────────────────────────────────────────────────────────────

export function OrderDetailSheet({ open, onOpenChange, store, currentRole, dark }) {
  const [customerExpanded, setCustomerExpanded] = useState(false);
  const [notizEdit, setNotizEdit]               = useState(false);
  const [notizDraft, setNotizDraft]             = useState('');
  const [editMode, setEditMode]                 = useState(false);
  const [editDatum, setEditDatum]               = useState('');
  const [editChannelId, setEditChannelId]       = useState('');
  const labelInputRef                            = useRef(null);
  const toast   = useToast();
  const confirm = useConfirm();
  // Datum/Kanal-Edit + Löschen nur owner/admin (Helfer server-seitig auf status/tracking begrenzt).
  const isAdmin = currentRole === 'owner' || currentRole === 'admin';


  // Always read from live orders so optimistic updates reflect immediately
  const order = store.detailOrder
    ? (store.orders ?? []).find(o => o.id === store.detailOrder.id) ?? store.detailOrder
    : null;

  function handleClose() {
    store.closeDetail?.();
    onOpenChange(false);
  }

  function handlePrimaryAction() {
    if (!order) return;
    const newStatus = STATUS_NEXT[order.status];
    if (!newStatus) return;
    store.updateOrder(order.id, 'status', newStatus);
  }

  function handleBearbeiten() {
    if (!order) return;
    setEditDatum(order.datum ? String(order.datum).slice(0, 10) : '');
    setEditChannelId(order.channelId || '');
    setEditMode(true);
  }

  function handleEditSave() {
    if (!order) return;
    const newDatum = editDatum || null;
    const oldDatum = order.datum ? String(order.datum).slice(0, 10) : null;
    if (newDatum && newDatum !== oldDatum) store.updateOrder(order.id, 'datum', newDatum);
    if (editChannelId && editChannelId !== order.channelId) store.changeOrderChannel(order.id, editChannelId);
    setEditMode(false);
    toast.success('Bestellung aktualisiert');
  }

  async function handleDelete() {
    if (!order) return;
    const ok = await confirm({
      title: 'Bestellung löschen?',
      subtitle: 'Kann nicht rückgängig gemacht werden.',
      confirmLabel: 'Löschen',
      destructive: true,
    });
    if (!ok) return;
    const success = await store.deleteOrder?.(order.id);
    if (success) { toast.success('Bestellung gelöscht'); handleClose(); }
  }

  function handleNotizOpen() {
    setNotizDraft(order?.notiz ?? '');
    setNotizEdit(true);
  }

  function handleNotizSave() {
    if (!order) return;
    store.updateOrder(order.id, 'notiz', notizDraft);
    setNotizEdit(false);
  }

  function handleLabelUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !order) return;
    store.setLabelForOrder?.(order.id, file);
    e.target.value = '';
  }

  function handleLabelClick() {
    labelInputRef.current?.click();
  }

  if (!order && open) return null;

  const primaryAction = order ? STATUS_ACTIONS[order.status] : null;
  const canWrite = CAN_WRITE(currentRole);

  // Dark / light style tokens
  const sheetBg    = dark ? '#2C2C2E' : '#FFFFFF';
  const textMain   = dark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.87)';
  const textMuted  = dark ? 'rgba(235,235,245,0.5)'  : 'rgba(60,60,67,0.6)';
  const cardBg     = dark ? 'rgba(44,44,46,1)'       : 'rgba(118,118,128,0.08)';
  const divider    = dark ? 'rgba(255,255,255,0.08)'  : 'rgba(60,60,67,0.12)';
  const cardBorder        = dark ? '1px solid var(--border-default)' : 'none';
  const cardBorderAccent  = dark ? '1px solid var(--tint-online)'    : 'none';
  const cardBorderSuccess = dark ? '1px solid var(--tint-stand)'     : 'none';
  const footerGrad = dark
    ? 'linear-gradient(to top, #2C2C2E 60%, transparent)'
    : 'linear-gradient(to top, #FFFFFF 60%, transparent)';
  const secondaryBtnBg = dark ? 'rgba(255,255,255,0.08)' : 'rgba(118,118,128,0.10)';

  const channelLabel = order ? (store.getChannelLabel?.(order.channelId) || order.kanal || '') : '';
  const statusLower  = order?.status?.toLowerCase() ?? 'offen';

  return (
    <>
      {open && order && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 2000,
              background: 'rgba(0,0,0,0.40)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              animation: 'fadeIn 0.2s ease',
            }}
            onClick={handleClose}
          />

          {/* Sheet panel */}
          <div
            data-testid="sheet-order-detail"
            style={{
              position: 'fixed', insetInline: 0, bottom: 0, zIndex: 2001,
              display: 'flex', flexDirection: 'column',
              maxHeight: '92vh',
              borderRadius: '24px 24px 0 0',
              background: sheetBg,
              paddingBottom: 'env(safe-area-inset-bottom)',
              color: textMain,
              animation: 'slideUpSheet 0.3s cubic-bezier(0.32, 0.72, 0, 1) backwards',
              willChange: 'transform',
            }}
          >
            {/* Handle */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 8 }}>
              <div className="bottom-sheet-handle" />
            </div>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
                  #{order.bestellnummer || order.id?.slice(0, 8)}
                </h2>
                <span className={`channel-pill channel-online`} style={{ fontSize: 11 }}>
                  {channelLabel}
                </span>
                <span className={`status-pill status-${statusLower}`}>
                  {order.status}
                </span>
              </div>
              <button
                data-testid="sheet-close-btn"
                className="ghost-icon-btn"
                onClick={handleClose}
                style={{ flexShrink: 0 }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px', paddingBottom: 120 }}>

              {/* Inline-Edit: Datum + Kanal (owner/admin) */}
              {editMode && (
                <div style={{ background: cardBg, borderRadius: 16, padding: 16, marginBottom: 16, border: cardBorder }}>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: textMuted, margin: '0 0 12px' }}>
                    Bearbeiten
                  </p>
                  <label style={{ fontSize: 13, color: textMuted, display: 'block', marginBottom: 6 }}>Datum</label>
                  <input
                    type="date"
                    value={editDatum}
                    onChange={e => setEditDatum(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', background: secondaryBtnBg, border: 'none', borderRadius: 10, padding: '10px 12px', color: textMain, fontSize: 14, marginBottom: 14 }}
                  />
                  <label style={{ fontSize: 13, color: textMuted, display: 'block', marginBottom: 6 }}>Kanal</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    {(store.getActiveChannels?.() ?? []).map(ch => {
                      const active = ch.id === editChannelId;
                      return (
                        <button key={ch.id} onClick={() => setEditChannelId(ch.id)}
                          style={{
                            padding: '7px 14px', borderRadius: 9999, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                            background: active ? 'var(--tint-online)' : secondaryBtnBg,
                            color: active ? 'var(--accent-online)' : textMuted,
                          }}>
                          {ch.name}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setEditMode(false)}
                      style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: 'none', cursor: 'pointer', background: secondaryBtnBg, color: textMain, fontSize: 14, fontWeight: 600 }}>
                      Abbrechen
                    </button>
                    <button onClick={handleEditSave}
                      style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--accent-online)', color: '#fff', fontSize: 14, fontWeight: 600 }}>
                      Speichern
                    </button>
                  </div>
                </div>
              )}

              {/* Date */}
              {order.datum && (
                <p style={{ fontSize: 13, color: textMuted, marginBottom: 20 }}>
                  {formatDate(order.datum)}
                </p>
              )}

              {/* Customer / Address block */}
              <div style={{ background: cardBg, borderRadius: 16, marginBottom: 12, overflow: 'hidden', border: cardBorderAccent }}>
                <button
                  onClick={() => setCustomerExpanded(v => !v)}
                  style={{
                    display: 'flex', width: '100%', alignItems: 'center',
                    justifyContent: 'space-between', padding: 16,
                    background: 'none', border: 'none', cursor: 'pointer', color: textMain,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%',
                      background: dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <MapPin size={18} color={textMuted} />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>Kundendetails</p>
                      <p style={{ margin: 0, fontSize: 13, color: textMuted }}>
                        {order.adresse ? 'Adresse vorhanden' : 'Keine Adresse'}
                      </p>
                    </div>
                  </div>
                  {customerExpanded
                    ? <ChevronUp size={18} color={textMuted} />
                    : <ChevronDown size={18} color={textMuted} />
                  }
                </button>

                <div
                  style={{
                    overflow: 'hidden',
                    maxHeight: customerExpanded ? '500px' : '0',
                    opacity: customerExpanded ? 1 : 0,
                    transition: 'max-height 0.2s ease, opacity 0.2s ease',
                  }}
                >
                  <div style={{ borderTop: `1px solid ${divider}`, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {order.adresse ? (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14 }}>
                        <MapPin size={15} color={textMuted} style={{ marginTop: 2, flexShrink: 0 }} />
                        <span style={{ color: textMain, whiteSpace: 'pre-line' }}>{order.adresse}</span>
                      </div>
                    ) : (
                      <p style={{ fontSize: 13, color: textMuted, margin: 0 }}>Keine Adresse hinterlegt</p>
                    )}
                    {order.trackingNumber && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                        <Truck size={15} color={textMuted} style={{ flexShrink: 0 }} />
                        <span style={{ color: textMain, fontFamily: 'monospace', fontSize: 13 }}>{order.trackingNumber}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Order summary */}
              <div style={{ background: cardBg, borderRadius: 16, padding: 16, marginBottom: 12, border: cardBorder }}>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: textMuted, margin: '0 0 12px' }}>
                  Bestellung
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, color: textMuted }}>Bestellnummer</span>
                    <span style={{ fontSize: 14, color: textMain, fontWeight: 500 }}>
                      #{order.bestellnummer || '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, color: textMuted }}>Kanal</span>
                    <span className={`channel-pill channel-online`} style={{ fontSize: 11 }}>
                      {channelLabel}
                    </span>
                  </div>
                  <div style={{ height: 1, background: divider, margin: '4px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: textMain }}>Gesamt</span>
                    <span style={{ fontSize: 17, fontWeight: 700, color: textMain }}>
                      {fmtCurrency(order.preis)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Notiz section */}
              <div style={{ background: cardBg, borderRadius: 16, padding: 16, marginBottom: 12, border: cardBorder }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: notizEdit ? 10 : 0 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: textMuted, margin: 0 }}>
                    Notiz
                  </p>
                  {canWrite && !notizEdit && (
                    <button
                      onClick={handleNotizOpen}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-online)', fontSize: 13, padding: 0 }}
                    >
                      {order.notiz ? 'Bearbeiten' : 'Hinzufügen'}
                    </button>
                  )}
                </div>

                {notizEdit ? (
                  <div>
                    <textarea
                      value={notizDraft}
                      onChange={e => setNotizDraft(e.target.value)}
                      rows={3}
                      placeholder="Notiz eingeben…"
                      autoFocus
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                        border: `1px solid ${divider}`,
                        borderRadius: 10, padding: '10px 12px',
                        fontSize: 14, color: textMain, resize: 'none',
                        outline: 'none', fontFamily: 'inherit',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setNotizEdit(false)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, fontSize: 14, padding: '6px 12px' }}
                      >
                        Abbrechen
                      </button>
                      <button
                        onClick={handleNotizSave}
                        style={{ background: 'var(--accent-online)', border: 'none', cursor: 'pointer', color: '#fff', fontSize: 14, fontWeight: 600, padding: '6px 14px', borderRadius: 8 }}
                      >
                        Speichern
                      </button>
                    </div>
                  </div>
                ) : (
                  <p style={{ margin: notizEdit ? 0 : '8px 0 0', fontSize: 14, color: order.notiz ? textMain : textMuted, fontStyle: order.notiz ? 'normal' : 'italic' }}>
                    {order.notiz || 'Keine Notiz'}
                  </p>
                )}
              </div>

              {/* Retoure badge */}
              {order.retoureErhalten && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'rgba(255,69,58,0.10)', borderRadius: 12,
                  padding: '10px 14px', marginBottom: 12,
                }}>
                  <RotateCcw size={16} color="var(--accent-red)" />
                  <span style={{ fontSize: 14, color: 'var(--accent-red)', fontWeight: 600 }}>Retoure erhalten</span>
                </div>
              )}

              {/* Label upload */}
              <div style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: textMuted, margin: '0 0 10px' }}>
                  Versandetikett
                </p>
                <input
                  ref={labelInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                  onChange={handleLabelUpload}
                />
                {order.labelKey ? (
                  <div style={{
                    background: cardBg, borderRadius: 16,
                    padding: 16, display: 'flex', alignItems: 'center', gap: 12,
                    border: cardBorderSuccess,
                  }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 10,
                      background: 'rgba(48,209,88,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Package size={22} color="var(--accent-stand)" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: textMain }}>Label vorhanden</p>
                      <p style={{ margin: 0, fontSize: 12, color: textMuted }}>{order.labelMime ?? 'Versandetikett'}</p>
                    </div>
                    {canWrite && (
                      <button
                        onClick={handleLabelClick}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-online)', fontSize: 13, padding: 0 }}
                      >
                        Ersetzen
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={canWrite ? handleLabelClick : undefined}
                    disabled={!canWrite}
                    style={{
                      display: 'block', width: '100%', boxSizing: 'border-box',
                      background: cardBg, borderRadius: 16,
                      border: `2px dashed ${dark ? 'rgba(255,255,255,0.15)' : 'rgba(60,60,67,0.20)'}`,
                      padding: 24, cursor: canWrite ? 'pointer' : 'default',
                      opacity: canWrite ? 1 : 0.5,
                      transition: 'border-color 150ms',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%',
                        background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Camera size={24} color={textMuted} />
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: textMain }}>Etikett fotografieren</p>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: textMuted }}>oder Datei hochladen</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Upload size={13} color={textMuted} />
                        <span style={{ fontSize: 12, color: textMuted }}>JPG, PNG, PDF bis 10 MB</span>
                      </div>
                    </div>
                  </button>
                )}
              </div>

              {/* Secondary actions grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
                <SecondaryBtn icon={Printer} label="Drucken" bg={secondaryBtnBg} textColor={textMuted} onClick={() => {}} />
                {isAdmin && (
                  <SecondaryBtn icon={Edit3} label="Bearbeiten" bg={secondaryBtnBg} textColor={textMuted} onClick={handleBearbeiten} />
                )}
                <SecondaryBtn icon={MessageSquare} label="Notiz" bg={secondaryBtnBg} textColor={textMuted} onClick={canWrite ? handleNotizOpen : undefined} />
                <SecondaryBtn icon={MoreHorizontal} label="Mehr" bg={secondaryBtnBg} textColor={textMuted} onClick={() => {}} />
              </div>

              {/* Löschen — nur owner/admin (server enforced via can(delete)) */}
              {isAdmin && (
                <button
                  onClick={handleDelete}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    width: '100%', marginTop: 4, padding: '12px 0', borderRadius: 12,
                    border: 'none', cursor: 'pointer', background: 'transparent',
                    color: 'var(--accent-red)', fontSize: 14, fontWeight: 600,
                  }}
                >
                  <Trash2 size={16} /> Bestellung löschen
                </button>
              )}
            </div>

            {/* Fixed footer — nur "Retoure starten" bleibt als manueller Workflow.
                Offen→Bereit nur via Label-Upload; Bereit→Versendet nur via Scanner. */}
            {primaryAction && canWrite && order.status === 'Versendet' && (
              <div style={{
                position: 'absolute', insetInline: 0, bottom: 0,
                padding: '40px 20px 20px',
                background: footerGrad,
              }}>
                <button
                  onClick={handlePrimaryAction}
                  style={{
                    display: 'block', width: '100%',
                    background: primaryAction.bg,
                    color: '#fff',
                    border: 'none', borderRadius: 16,
                    padding: '16px 0', fontSize: 16, fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'transform 100ms, opacity 100ms',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                  }}
                  onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.98)')}
                  onMouseUp={e => (e.currentTarget.style.transform = '')}
                  onTouchStart={e => (e.currentTarget.style.transform = 'scale(0.98)')}
                  onTouchEnd={e => (e.currentTarget.style.transform = '')}
                >
                  {primaryAction.label}
                </button>
              </div>
            )}

            {/* Viewer/finance: read-only hint at bottom */}
            {!canWrite && (
              <div style={{ padding: '12px 20px 20px', textAlign: 'center' }}>
                <span style={{ fontSize: 12, color: textMuted }}>Lesezugriff — keine Änderungen möglich</span>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function SecondaryBtn({ icon: Icon, label, bg, textColor, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 6, padding: 12, borderRadius: 12,
        background: bg, border: 'none', cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 100ms',
      }}
      onMouseDown={e => onClick && (e.currentTarget.style.transform = 'scale(0.94)')}
      onMouseUp={e => (e.currentTarget.style.transform = '')}
      onTouchStart={e => onClick && (e.currentTarget.style.transform = 'scale(0.94)')}
      onTouchEnd={e => (e.currentTarget.style.transform = '')}
    >
      <Icon size={20} color={textColor} />
      <span style={{ fontSize: 10, color: textColor, fontWeight: 500 }}>{label}</span>
    </button>
  );
}
